import { useRouter } from '@tanstack/react-router';
import {
  EmptyState,
  EmptyStateTitle,
  Link,
  PageLoader,
  Panel,
  PanelActions,
  PanelDescription,
  PanelHeader,
  PanelInset,
  PanelTitle,
  Paragraph,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import { useLogs } from '@/services/db/activity-log/queries';
import { customerDisplay } from '@/services/db/customer/display';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { FormattedDateTime } from '@customerio/pluma-components/react';
import { getActivityLogType } from '../utils/item';
import type { DataIndexItemAddress } from '../urls';

/**
 * The last few things that happened to this item.
 *
 * Five rows, because the point is "is this still arriving?" rather than a
 * feed — the full log is one click away, already narrowed to this item.
 */

/** How many rows the panel shows before sending you to the full log. */
const RECENT_LIMIT = 5;

type RecentActivityProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
};

export function RecentActivity({ workspaceId, address }: RecentActivityProps) {
  const router = useRouter();
  const taxonomy = useProductTaxonomy();
  const type = getActivityLogType(
    address.scope.kind === 'profile'
      ? { id: address.name, kind: address.kind === 'event' ? 'event' : 'attribute' }
      : {
          id: address.name,
          kind: address.scope.kind === 'relationship' ? 'object-relationship-attribute' : 'object-attribute',
          object_type_id: address.scope.objectTypeId,
        },
  );

  const logs = useLogs({
    environmentId: String(workspaceId),
    tab: 'identified',
    filters: { type, name: address.name, from: '', to: '' },
    autoRefresh: false,
    limit: RECENT_LIMIT,
  });

  const rows = logs.rows;
  const isObject = address.scope.kind === 'object';
  const subjectHeader = isObject ? 'Object' : taxonomy.term('people.titleSingular');

  // The path comes from the router; the query is appended by hand. An item's
  // name is customer-authored, and `buildLocation` writes search values
  // through `JSON.stringify` — a name like `2024` would arrive at the log as
  // the three-character string `"2024"`, which matches nothing.
  const fullLogPath = router.buildLocation({
    to:
      taxonomy.variant === 'future'
        ? '/workspaces/$workspaceId/journeys/profile_activity/$tab'
        : '/workspaces/$workspaceId/journeys/activity_logs/$tab',
    params: { workspaceId: String(workspaceId), tab: 'identified' },
  }).href;
  const fullLogHref = `${fullLogPath}?${new URLSearchParams({ type, name: address.name }).toString()}`;

  return (
    <Panel>
      <PanelHeader withBorder>
        <PanelTitle>Recent Activity</PanelTitle>
        <PanelDescription>Reflects only the last 30 days of activity</PanelDescription>
        <PanelActions>
          <Link
            href={fullLogHref}
            icon="exit"
            iconSize="sm"
            iconPosition="trailing"
            isUnderlined={false}
            target="_blank"
          >
            See full {taxonomy.t('activityLogs.term')}
          </Link>
        </PanelActions>
      </PanelHeader>
      <PanelInset>
        <Table caption={`Activity for ${address.name}`}>
          <TableThead>
            <TableTr>
              <TableTh>{subjectHeader}</TableTh>
              <TableTh textAlign="right">Timestamp</TableTh>
            </TableTr>
          </TableThead>
          <TableTbody>
            <RecentActivityRows
              rows={rows}
              isLoading={logs.isLoading}
              isError={logs.isError}
              personSingular={taxonomy.term('people.titleSingular')}
            />
          </TableTbody>
        </Table>
      </PanelInset>
    </Panel>
  );
}

type RecentActivityRowsProps = {
  rows: ReturnType<typeof useLogs>['rows'];
  isLoading: boolean;
  isError: boolean;
  personSingular: string;
};

function RecentActivityRows({ rows, isLoading, isError, personSingular }: RecentActivityRowsProps) {
  if (isLoading) {
    return (
      <TableTr>
        <TableTd colSpan={2}>
          <PageLoader label="Loading…" />
        </TableTd>
      </TableTr>
    );
  }

  if (isError) {
    return (
      <TableTr>
        <TableTd colSpan={2}>
          <Paragraph variant="secondary">Could not load recent activity</Paragraph>
        </TableTd>
      </TableTr>
    );
  }

  if (rows.length === 0) {
    return (
      <TableTr>
        <TableTd colSpan={2}>
          <EmptyState>
            <EmptyStateTitle icon="activity">No recent activity</EmptyStateTitle>
          </EmptyState>
        </TableTd>
      </TableTr>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <TableTr key={row.log.id}>
          <TableTd>
            {/* A profile's name or address is subscriber data. */}
            <Text className="fs-mask">
              {row.object?.attributes?.name ??
                (row.customer == null ? (row.log.customer_id ?? '—') : customerDisplay(row.customer, personSingular))}
            </Text>
          </TableTd>
          <TableTd textAlign="right">
            <FormattedDateTime date={row.log.timestamp * 1000} />
          </TableTd>
        </TableTr>
      ))}
    </>
  );
}
